import type { MailDomainRecord } from "@/api";

/**
 * The prompt a business hands to whatever assistant it already has open (#1330, part of #1322).
 *
 * The registrar-specific half of "publish these two records" is the half we cannot write: Strato,
 * IONOS, GoDaddy, Cloudflare and All-Inkl name and nest DNS settings differently, and a walkthrough
 * of each one goes stale on its own as those panels are redesigned. So the console writes the part
 * it knows exactly — the records, what they are for, and what must not change — and asks the
 * assistant to supply the part that depends on where the domain happens to live.
 *
 * **What this may see is the whole safety argument.** It takes a domain and its records, not a
 * `MailDomainView`, so the claim id, the address, the check outcome and the timestamps are not in
 * scope here — not "not used", but unreachable. Everything it can see is already destined for
 * public DNS: the records are published by definition, and the verification token proves control of
 * a zone rather than granting it. That is what makes pasting this into a third party's assistant
 * safe, and it is also the boundary. If this prompt ever needs a value that is not public DNS data,
 * that is a signal to stop rather than to widen the input.
 */
export interface MailDomainPromptSource {
  readonly domain: string;
  readonly records: readonly MailDomainRecord[];
}

/** `t` from the i18n context, or `resolveCopy` bound to a language in a test. */
export type PromptTranslate = (
  key: string,
  vars?: Record<string, string | number>,
) => string;

/**
 * What each record is for, said in the prompt so the assistant explains rather than dictates.
 *
 * Keyed by the record's `kind` rather than its position, and a kind with no key contributes no
 * line — a third record added upstream then arrives in the prompt unexplained rather than
 * described as something it is not.
 */
const PURPOSE_KEY: Record<string, string> = Object.assign(Object.create(null), {
  mx: "mailDomain.prompt.purpose.mx",
  txt: "mailDomain.prompt.purpose.txt",
  returnPath: "mailDomain.prompt.purpose.returnPath",
  dkim: "mailDomain.prompt.purpose.dkim",
});

function recordLines(records: readonly MailDomainRecord[], t: PromptTranslate): string[] {
  const lines: string[] = [];
  records.forEach((record, index) => {
    const vars = {
      n: index + 1,
      type: record.type,
      name: record.name,
      value: record.value,
      priority: record.priority ?? "",
    };
    lines.push(
      record.priority === null
        ? t("mailDomain.prompt.record.plain", vars)
        : t("mailDomain.prompt.record.withPriority", vars),
    );
    const purpose = PURPOSE_KEY[record.kind];
    if (purpose) lines.push(t(purpose));
  });
  return lines;
}

/**
 * The prompt, as plain text ready for the clipboard.
 *
 * The order is deliberate. The assistant is told to ask where the domain is registered *before*
 * giving steps, because a confident answer for the wrong control panel is worse than a question:
 * it sends someone hunting for a menu that does not exist and ends with "this product is broken".
 * The limits come after the records and before the checking, because that is where an assistant is
 * most likely to volunteer a helpful change to a record nobody asked about.
 *
 * The limits say "only the records listed above" rather than "only the subdomain", because the
 * claim is not guaranteed to be one. `isClaimableMailDomain` accepts any name of two labels or
 * more, so a business can claim its own apex, and there the MX above *is* the apex record — a
 * prompt that called it a subdomain would contradict the records it had just listed, and an
 * assistant resolving that contradiction in favour of "use the values verbatim" would take the
 * business's real mail down. So instead of a promise we cannot keep, the assistant is asked to
 * look for an existing MX at that name and stop if it finds one. That is a check we cannot do
 * from here and it can.
 */
export function mailDomainAiPrompt(source: MailDomainPromptSource, t: PromptTranslate): string {
  const domain = { domain: source.domain };
  // Receiving and sending are published in separate steps (#1424), and the prompt follows the one
  // it was built for. Which one is read off the records themselves rather than passed in, so the
  // input stays public DNS data and nothing else: an MX is the receiving step, and the guard about
  // an existing MX only makes sense beside one.
  const receiving = source.records.some((record) => record.kind === "mx");
  const blocks: string[][] = [
    [t(receiving ? "mailDomain.prompt.intro" : "mailDomain.prompt.introSend", domain)],
    [
      t("mailDomain.prompt.orderHeading"),
      t("mailDomain.prompt.step.askRegistrar", domain),
      t("mailDomain.prompt.step.thenSteps"),
    ],
    [t("mailDomain.prompt.recordsHeading"), ...recordLines(source.records, t)],
    [
      t("mailDomain.prompt.limitsHeading"),
      t("mailDomain.prompt.limit.listedOnly", domain),
      ...(receiving ? [t("mailDomain.prompt.limit.existingMx")] : []),
      t("mailDomain.prompt.limit.nothingElse"),
      t("mailDomain.prompt.limit.trailingDot"),
    ],
    [
      t("mailDomain.prompt.afterHeading"),
      t("mailDomain.prompt.after.verify"),
      t("mailDomain.prompt.after.propagation"),
    ],
  ];
  return blocks.map((block) => block.join("\n")).join("\n\n");
}
